/**
 * T-033 — webhooks, change feed, service-mode tickets, users lookup contracts (docs/10-design/08 §2, §5 · tasks/W3-CONTRACTS.md §6)
 * `Ticket`ที่ index อ้างผ่าน z.lazy กัน circular TDZ (แบบเดียวกับ handoff.ts)
 */
import { z } from "zod";
import { Message, TicketSummary } from "./index.ts";

/** ชื่อ scope ของ api key (ตรึงตัว — W3-CONTRACTS §6) */
export const API_SCOPES = [
  "email:send",
  "email:send:subject",
  "email:read:subject",
  "email:read",
  "email:control",
  "email:suppressions",
  "tickets:read",
  "tickets:write",
  "notes:read",
  // [T-330] เขียน internal note ผ่าน act-as — แยกจากการอ่าน (notes:read ของ T-332)
  "notes:write",
  "attachments:write",
  "act-as",
  "users:lookup",
  "events:read",
  "webhooks:manage",
  "embed:tokens",
  "forms:submit",
  // [T-330] catalog ตรง contract จริง — requireMailPrincipal (mail/api/routes.ts) บังคับสอง scope นี้ต่อ route
  // อยู่แล้วแต่ไม่เคยอยู่ใน API_SCOPES → admin UI/API สร้าง key ให้ไม่ได้ (scopes ⊆ API_SCOPES)
  "mail:read",
  "mail:send",
] as const;
export type ApiScope = (typeof API_SCOPES)[number];

export const WebhookFilters = z
  .object({
    service_codes: z.array(z.string()).max(50).optional(),
    team_ids: z.array(z.uuid()).max(50).optional(),
    tags: z.array(z.string()).max(50).optional(),
  })
  .strict();
export type WebhookFilters = z.infer<typeof WebhookFilters>;

export const WebhookInput = z.object({
  url: z.string().min(1).max(2048),
  events: z.array(z.string().min(1).max(64)).min(1).max(64),
  filters: WebhookFilters.optional(),
  description: z.string().max(500).nullable().optional(),
  enabled: z.boolean().optional(),
});
export type WebhookInput = z.infer<typeof WebhookInput>;

export const WebhookPatch = WebhookInput.partial();
export type WebhookPatch = z.infer<typeof WebhookPatch>;

export const Webhook = z.object({
  id: z.uuid(),
  client_id: z.uuid().nullable(),
  url: z.string(),
  events: z.array(z.string()),
  filters: WebhookFilters,
  enabled: z.boolean(),
  description: z.string().nullable(),
  failure_streak: z.number().int(),
  disabled_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type Webhook = z.infer<typeof Webhook>;

/** ตอบครั้งเดียวตอนสร้าง/rotate — `secret` ไม่เก็บ plaintext ใน DB (R6 · D-xxx: เข้ารหัสด้วย SESSION_ENC_KEY แทน hash) */
export const WebhookCreated = Webhook.extend({ secret: z.string() });
export type WebhookCreated = z.infer<typeof WebhookCreated>;

export const WebhookDelivery = z.object({
  id: z.uuid(),
  webhook_id: z.uuid(),
  event_id: z.number().int().nullable(),
  event_type: z.string(),
  attempt: z.number().int(),
  status_code: z.number().int().nullable(),
  duration_ms: z.number().int().nullable(),
  response_snippet: z.string().nullable(),
  error: z.string().nullable(),
  at: z.iso.datetime(),
});
export type WebhookDelivery = z.infer<typeof WebhookDelivery>;

/** Actual webhook list limits; these surfaces currently return next_cursor:null. */
export const WebhookListQuery = z.object({ limit: z.coerce.number().int().min(1).max(100).default(50) });
export const WebhookDeliveriesQuery = z.object({ limit: z.coerce.number().int().min(1).max(200).default(50) });
export const WebhookList = z.object({ items: z.array(Webhook), next_cursor: z.literal(null) });
export const WebhookDeliveryList = z.object({ items: z.array(WebhookDelivery), next_cursor: z.literal(null) });
export const WebhookDeleted = z.object({ deleted: z.literal(true) });
export const WebhookTestResult = z.object({ sent: z.boolean() });

export const ExternalEventActor = z.object({
  type: z.string(),
  id: z.string().nullable().optional(),
  service: z.string().nullable().optional(),
  act_as: z.string().nullable().optional(),
});
export type ExternalEventActor = z.infer<typeof ExternalEventActor>;

/** ชื่อ event ที่ออกภายนอก (change feed/webhook) — mapping จาก ticket_events.type ที่ D-031 §3 */
export const ExternalEvent = z.object({
  id: z.string(),
  type: z.string(),
  occurred_at: z.iso.datetime(),
  api_version: z.string(),
  actor: ExternalEventActor,
  data: z.object({
    ticket: z.lazy(() => TicketSummary),
    message: z.lazy(() => Message).optional(),
    changes: z.record(z.string(), z.unknown()).optional(),
  }),
  previous: z.record(z.string(), z.unknown()).optional(),
});
export type ExternalEvent = z.infer<typeof ExternalEvent>;

export const EventFeed = z.object({
  items: z.array(ExternalEvent),
  next_cursor: z.number().int().nullable(),
});
export type EventFeed = z.infer<typeof EventFeed>;

/** POST /api/v1/service/tickets (api key service mode — ไม่มี act-as) — R3 */
export const CreateTicketApi = z
  .object({
    service_code: z
      .string()
      .regex(/^[A-Z0-9]{2,4}$/)
      .optional(),
    subject: z.string().trim().min(1).max(998),
    body_html: z.string().max(200_000).optional(),
    body_text: z.string().max(200_000).optional(),
    requester: z.union([
      z.object({ email: z.email(), name: z.string().max(120).optional() }).strict(),
      z.object({ sso_sub: z.uuid(), name: z.string().trim().min(1).max(120).optional() }).strict(),
    ]),
    cc: z.array(z.email()).max(48).optional(),
    priority_code: z.string().optional(),
    tags: z.array(z.string().max(64)).max(20).optional(),
    custom_fields: z.record(z.string(), z.unknown()).optional(),
    external_ref: z.string().max(255).optional(),
    imported: z.boolean().optional(),
    /** [T-074 · #213] ไฟล์แนบที่อัปโหลดผ่าน /api/v1/service/attachments แล้ว (status=ready ของ client นี้เท่านั้น) */
    attachment_ids: z.array(z.uuid()).max(20).optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.body_html && !v.body_text) ctx.addIssue({ code: "custom", path: ["body_html"], message: "ต้องมี body_html หรือ body_text" });
  });
export type CreateTicketApi = z.infer<typeof CreateTicketApi>;

/** [T-074 · #213] GET /api/v1/service/tickets — cursor keyset (created_at, id) แบบเดียวกับ list อื่น */
export const ServiceTicketListQuery = z.object({
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ServiceTicketListQuery = z.infer<typeof ServiceTicketListQuery>;

/** [T-074 · #213] POST /api/v1/service/tickets/:id/messages — ข้อความ public ในนาม requester (ลูกค้า) */
export const ServiceTicketReply = z
  .object({
    body_html: z.string().max(500_000).optional(),
    body_text: z.string().max(500_000).optional(),
    attachment_ids: z.array(z.uuid()).max(20).optional(),
    expected_version: z.number().int().optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.body_html && !v.body_text) ctx.addIssue({ code: "custom", path: ["body_html"], message: "ต้องมี body_html หรือ body_text" });
  });
export type ServiceTicketReply = z.infer<typeof ServiceTicketReply>;

/** [T-074 · #213] POST /api/v1/service/attachments/presign — scope attachments:write */
export const ServiceAttachmentPresign = z.object({
  filename: z.string().trim().min(1).max(255),
  size: z.number().int().min(1).max(25 * 1024 * 1024),
  mime: z.string().max(255).optional(),
  ticket_id: z.string().max(32).optional(),
});
export type ServiceAttachmentPresign = z.infer<typeof ServiceAttachmentPresign>;

/** [T-074 · #213] ผล presign — client PUT ไบต์ดิบไปที่ upload_url (แนวทาง design 08 §3) */
export const ServiceAttachmentPresigned = z.object({
  attachment_id: z.uuid(),
  upload_url: z.string(),
  headers: z.record(z.string(), z.string()),
  expires_at: z.iso.datetime(),
});
export type ServiceAttachmentPresigned = z.infer<typeof ServiceAttachmentPresigned>;

/** [T-074 · #213] POST /api/v1/service/attachments/:id/complete — sniff แล้วพร้อมใช้ */
export const ServiceAttachmentReady = z.object({
  id: z.uuid(),
  filename: z.string(),
  mime: z.string(),
  size: z.number().int(),
  risk_flag: z.boolean(),
  status: z.literal("ready"),
});
export type ServiceAttachmentReady = z.infer<typeof ServiceAttachmentReady>;

/** [T-074 · #213] GET /api/v1/service/attachments/:id/url — signed URL อายุ 10 นาที (design 08 §3) */
export const ServiceAttachmentUrl = z.object({
  url: z.string(),
  expires_at: z.iso.datetime(),
});
export type ServiceAttachmentUrl = z.infer<typeof ServiceAttachmentUrl>;

export const UserLookup = z.object({
  id: z.uuid(),
  primary_email: z.string().nullable(),
  display_name: z.string().nullable(),
  kind: z.enum(["staff", "customer", "guest", "service"]),
});
export type UserLookup = z.infer<typeof UserLookup>;
